import ProgressbarColors from '@helpers/progressbar-colors';
import { Box, CircularProgress, Typography, useMediaQuery } from '@mui/material';
import PropTypes from 'prop-types';

function getDataToDisplay(displayMode, rating, max) {
    const data = { display: max > 0, value: 0, label: 'n/a' };

    if (!data.display) return data;

    const parsedRating = parseFloat(rating);
    const value = isNaN(parsedRating) ? 0 : Number(((parsedRating / max) * 100).toFixed(2));
    data.value = value;

    switch (displayMode) {
        case 'percent':
            data.label = `${data.value}%`;
            break;
        case 'fraction':
            data.label = `${rating}/${max}`;
            break;
        default:
            break;
    }

    return data;
}

function useGetSize(small, upTo = 'xl') {
    const breakpointUp = useMediaQuery(theme => theme.breakpoints.up(upTo));

    const defaults = small ? { min: 10, max: 13 } : { min: 16, max: 20 };

    return breakpointUp ? defaults.max : defaults.min;
}

/**
 * CircularRatingWithInnerLabel displays a circular progress bar with a label inside.
 * @param rating{number|string} - The rating to display.
 * @param max{number|string} - The Max rating (optional). Default 100.
 * @param displayMode{'percent' | 'fraction'} - The view of displayed rating (optional). Default 'percent'.
 * @param label{string} - The label to display.
 * @param prevRating{number|string} - The previous rating to display (optional).
 * @param fillColor{string} - The color of the progress bar (optional).
 * @param innerLabel{JSX.Element | string} - The inner label to display instead of label and rating value (optional).
 * @param small{boolean} - Whether to display the component in a smaller size.
 * @param dark{boolean} - Whether to use dark colors.
 * @param sx{Object} - Additional styles to apply.
 * @returns {JSX.Element}
 */
function CircularRatingWithInnerLabel({
    rating,
    max = 100,
    displayMode = 'percent', // 'percent' | 'fraction'
    prevRating = null,
    small = false,
    upTo,
    dark = false,
    fillColor = null,
    label,
    innerLabel = null,
    sx,
}) {
    const { display, value, label: displayValue } = getDataToDisplay(displayMode, rating, max);
    const size = useGetSize(small, upTo);

    return (
        <Box
            sx={[
                {
                    alignItems: 'center',
                    display: 'flex',
                    justifyContent: 'center',
                    position: 'relative',
                },
                ...(Array.isArray(sx) ? sx : [sx]),
            ]}
        >
            <CircularProgress
                aria-label={label}
                variant='determinate'
                sx={theme => ({
                    color: theme.palette.grey[300],
                })}
                size={size + 'rem'}
                thickness={4}
                value={100}
            />
            {display && (
                <CircularProgress
                    aria-label={label}
                    thickness={4}
                    size={size + 'rem'}
                    sx={{
                        'position': 'absolute',
                        '& .MuiCircularProgress-svg': {
                            strokeLinecap: 'round',
                            backgroundColor: 'transparent',
                            color: fillColor ?? `${ProgressbarColors(value, dark)}`,
                        },
                    }}
                    value={value}
                    variant='determinate'
                />
            )}
            <Box
                sx={{
                    width: '100%',
                    height: '100%',
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: display ? 'black' : 'darkgray',
                }}
            >
                {innerLabel ?? (
                    <>
                        {label ? (
                            <Typography
                                sx={{
                                    fontSize: small ? '0.85rem' : { xs: '0.85rem', xl: '1rem' },
                                    fontWeight: 'bold',
                                }}
                            >
                                {label}
                            </Typography>
                        ) : null}

                        <Typography
                            sx={{
                                fontSize: {
                                    xs: small ? '1.6rem' : '2.6rem',
                                    md: small ? '2rem' : '3rem',
                                    xl: small ? '2.6rem' : '3.3rem',
                                },
                                fontWeight: 'bold',
                            }}
                        >
                            {displayValue}
                        </Typography>

                        {prevRating && (
                            <Typography
                                sx={{
                                    fontSize: small ? '1.2rem' : { xs: '1.2rem', xl: '1.4rem' },
                                    color: 'text.secondary',
                                    textDecoration: 'line-through',
                                    fontWeight: 500,
                                }}
                            >
                                {prevRating}%
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}

CircularRatingWithInnerLabel.propTypes = {
    label: PropTypes.string,
    rating: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    small: PropTypes.bool,
    dark: PropTypes.bool,
    sx: PropTypes.object,
};

export default CircularRatingWithInnerLabel;
